/**
 * Default register form component
 * Features: Entity name input + manual confirmation
 */
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { updateServiceEntityName } from '@/modules/Service/services'
import { resolveInternalTicketWorkflowByStepId } from '@/modules/Service/services/ticket'
import { GridHookForm } from 'vnetwork-ui'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import type { RegisterServiceTemplateProps } from '../types'

const DefaultRegisterForm: React.FC<RegisterServiceTemplateProps> = ({
    service,
    stepId,
}) => {
    const { t } = useTranslation()
    const serviceID = service?.service_id as number
    const stepIdNumber = stepId as number
    const { refetch } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const { refetch: refetchServices } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })
    const formControl = useForm<{
        name: string
    }>({
        defaultValues: {
            name: service?.name || '',
        },
    })
    const { mutateAsync: updateEntityName, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateServiceEntityName },
            {
                successMessage: t('update-service-entity-name-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    refetchServices()
                },
            }
        )

    const { mutateAsync: resolveStep, isPending: isResolving } =
        useMutationWithHandler(
            { mutationFn: resolveInternalTicketWorkflowByStepId },
            {
                successMessage: t('step-resolved-success'),
                reactQueryKeys: QUERY_KEYS.TICKETS,
                onSuccess: () => {
                    refetch()
                },
            }
        )

    const onSubmit = async (values: { name: string }) => {
        await updateEntityName({
            serviceID: serviceID,
            name: values?.name,
        })
        await resolveStep({ stepId: stepIdNumber })
    }

    return (
        <div className="space-y-4">
            {stepId && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium mb-1">
                        {t('manual-service-creation-required')}
                    </p>
                    <p className="text-xs text-amber-600">
                        {t('please-create-service-manually-then-confirm')}
                    </p>
                </div>
            )}
            <div className="mt-2">
                <GridHookForm
                    formControl={formControl}
                    data={[
                        {
                            formItemProps: {
                                name: 'name',
                                type: 'textfield',
                                disabled: !!service?.name,
                                label: t('entity-name'),
                                placeholder: t('entity-name'),
                            },
                        },
                    ]}
                    actions={[
                        {
                            title: t('confirm-created'),
                            type: 'submit',
                            disabled:
                                !formControl.formState?.isDirty || isUpdating,
                            loading: isUpdating || isResolving,
                            variant: 'contained',
                        },
                    ]}
                    onSubmit={onSubmit}
                />
            </div>
        </div>
    )
}

export default DefaultRegisterForm
